import React from "react";

interface KioskErrorBoundaryProps {
  children: React.ReactNode;
  /** How long to wait before auto-recovering to the home screen (ms). */
  autoRecoverMs?: number;
}

interface KioskErrorBoundaryState {
  hasError: boolean;
}

const DEFAULT_AUTO_RECOVER_MS = 15000;

/**
 * Top-level error boundary for the kiosk. Because the device runs unattended,
 * an uncaught render error must never leave a permanent blank screen: we show a
 * friendly message and automatically reload back to the idle/home screen.
 */
class KioskErrorBoundary extends React.Component<
  KioskErrorBoundaryProps,
  KioskErrorBoundaryState
> {
  private recoverTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: KioskErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): KioskErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[KioskErrorBoundary] Uncaught error:", error, info);
    this.scheduleRecovery();
  }

  componentWillUnmount() {
    this.clearRecovery();
  }

  private scheduleRecovery() {
    this.clearRecovery();
    const delay = this.props.autoRecoverMs ?? DEFAULT_AUTO_RECOVER_MS;
    this.recoverTimer = setTimeout(() => this.handleRecover(), delay);
  }

  private clearRecovery() {
    if (this.recoverTimer) {
      clearTimeout(this.recoverTimer);
      this.recoverTimer = null;
    }
  }

  private handleRecover = () => {
    this.clearRecovery();
    // Full reload to "/" guarantees a clean state regardless of where the
    // error happened (React tree, Redux, router).
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 24,
          padding: 40,
          textAlign: "center",
          background: "#222326",
          color: "#fff",
          fontFamily: "inherit",
          zIndex: 99999,
        }}
      >
        <h1 style={{ fontSize: 36, margin: 0 }}>Something went wrong</h1>
        <p style={{ fontSize: 20, opacity: 0.8, maxWidth: 640 }}>
          We hit an unexpected problem. The kiosk will return to the home screen
          automatically.
        </p>
        <button
          onClick={this.handleRecover}
          style={{
            fontSize: 20,
            padding: "16px 48px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            background: "#fff",
            color: "#222326",
            fontWeight: 600,
          }}
        >
          Return to home
        </button>
      </div>
    );
  }
}

export default KioskErrorBoundary;
